import React, { useState, useEffect } from 'react';

const HRASubmissionList = () => {
    const [submissions, setSubmissions] = useState([]);
    const [filteredSubmissions, setFilteredSubmissions] = useState([]);
    const [currentPageSubmissions, setCurrentPageSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Search and Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [riskFilter, setRiskFilter] = useState('all');
    const [departmentFilter, setDepartmentFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [userFilter, setUserFilter] = useState('all');
    const [userStats, setUserStats] = useState({});
    const [showUserStats, setShowUserStats] = useState(false);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    // Fetch data from API
    const fetchSubmissions = async () => {
        try {
            setLoading(true);

            const token = localStorage.getItem("authToken");

            const response = await fetch(
                "https://api.elthiumhealth.com/api/admin/allhrasubmission",
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Server error: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                // Ensure data consistency and handle missing fields
                const safeData = Array.isArray(data.data)
                    ? data.data.map((submission) => ({
                        ...submission,
                        staff: submission.staff || {
                            name: "Unknown Staff",
                            email: "N/A",
                            employeeId: "N/A",
                            department: "Unknown Department",
                            branch: "N/A",
                            age: "N/A",
                            gender: "N/A",
                            mobile: "N/A",
                            company: "N/A",
                        },
                        categoryPoints: submission.categoryPoints || {},
                        prescribedForCategories:
                            submission.prescribedForCategories || {},
                        answers: submission.answers || [],
                        riskLevel: submission.riskLevel || "Unknown",
                        totalPoints: submission.totalPoints || 0,
                        riskMessage:
                            submission.riskMessage ||
                            "No risk assessment available",
                        submissionId:
                            submission.submissionId ||
                            `sub-${Date.now()}-${Math.random()}`,
                    }))
                    : [];

                setSubmissions(safeData);
                setFilteredSubmissions(safeData);
                calculateUserStats(safeData);
                setError("");
            } else {
                setError(data.message || "Failed to fetch data");
            }
        } catch (err) {
            console.error("Error fetching HRA submissions:", err);
            setError("Error connecting to server");
        } finally {
            setLoading(false);
        }
    };

    // Calculate user submission statistics
    const calculateUserStats = (submissionsData) => {
        const stats = {};

        submissionsData.forEach(submission => {
            const staff = submission.staff || {};
            const userId = staff.employeeId || staff.email || staff.name || 'unknown';

            if (!stats[userId]) {
                stats[userId] = {
                    name: staff.name || 'Unknown Staff',
                    email: staff.email || 'N/A',
                    employeeId: staff.employeeId || 'N/A',
                    department: staff.department || 'Unknown Department',
                    branch: staff.branch || 'N/A',
                    age: staff.age || 'N/A',
                    gender: staff.gender || 'N/A',
                    mobile: staff.mobile || 'N/A',
                    company: staff.company || 'N/A',
                    totalSubmissions: 0,
                    submissions: [],
                    avgTotalPoints: 0,
                    riskDistribution: {
                        high: 0,
                        medium: 0,
                        low: 0,
                        unknown: 0
                    },
                    departments: new Set(),
                    firstSubmission: null,
                    lastSubmission: null
                };
            }

            stats[userId].totalSubmissions++;
            stats[userId].submissions.push(submission);

            // Add department to set
            if (staff.department) {
                stats[userId].departments.add(staff.department);
            }

            // Track risk levels
            const riskLevel = (submission.riskLevel || 'unknown').toLowerCase();
            if (stats[userId].riskDistribution[riskLevel] !== undefined) {
                stats[userId].riskDistribution[riskLevel]++;
            } else {
                stats[userId].riskDistribution.unknown++;
            }

            // Track submission dates
            const submissionDate = new Date(submission.submittedAt);
            if (!stats[userId].firstSubmission || submissionDate < new Date(stats[userId].firstSubmission)) {
                stats[userId].firstSubmission = submission.submittedAt;
            }
            if (!stats[userId].lastSubmission || submissionDate > new Date(stats[userId].lastSubmission)) {
                stats[userId].lastSubmission = submission.submittedAt;
            }
        });

        // Calculate average points for each user
        Object.keys(stats).forEach(userId => {
            const user = stats[userId];
            if (user.submissions.length > 0) {
                const totalPoints = user.submissions.reduce((sum, sub) => sum + (sub.totalPoints || 0), 0);
                user.avgTotalPoints = Math.round(totalPoints / user.submissions.length);
            }
            // Convert Set to Array
            user.departments = Array.from(user.departments);
        });

        setUserStats(stats);
    };

    // Update pagination when filtered submissions change
    useEffect(() => {
        const totalPagesCount = Math.ceil(filteredSubmissions.length / itemsPerPage);
        setTotalPages(totalPagesCount);

        // Reset to page 1 if current page is beyond new total pages
        if (currentPage > totalPagesCount && totalPagesCount > 0) {
            setCurrentPage(1);
        }

        // Calculate current page items
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        const currentItems = filteredSubmissions.slice(indexOfFirstItem, indexOfLastItem);
        setCurrentPageSubmissions(currentItems);
    }, [filteredSubmissions, currentPage, itemsPerPage]);

    useEffect(() => {
        fetchSubmissions();
    }, []);

    // Apply filters and search
    useEffect(() => {
        let filtered = submissions;

        // User filter - show all submissions for selected user
        if (userFilter !== 'all') {
            filtered = filtered.filter(submission => {
                const staff = submission.staff || {};
                return (staff.employeeId || staff.email || staff.name) === userFilter;
            });
        }

        // Search filter with null checks - INCLUDES EMPLOYEE ID
        if (searchTerm) {
            filtered = filtered.filter(submission => {
                const staff = submission.staff || {};
                return (
                    staff.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    staff.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    staff.employeeId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    staff.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    staff.branch?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    staff.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    staff.company?.toLowerCase().includes(searchTerm.toLowerCase())
                );
            });
        }

        // Risk level filter with null check
        if (riskFilter !== 'all') {
            filtered = filtered.filter(submission =>
                submission.riskLevel?.toLowerCase() === riskFilter.toLowerCase()
            );
        }

        // Department filter with null check
        if (departmentFilter !== 'all') {
            filtered = filtered.filter(submission =>
                submission.staff?.department === departmentFilter
            );
        }

        // Date filter (last 7 days, last 30 days, etc.)
        if (dateFilter !== 'all') {
            const now = new Date();
            let startDate = new Date();

            switch (dateFilter) {
                case 'today':
                    startDate.setHours(0, 0, 0, 0);
                    break;
                case 'week':
                    startDate.setDate(now.getDate() - 7);
                    break;
                case 'month':
                    startDate.setDate(now.getDate() - 30);
                    break;
                case 'quarter':
                    startDate.setDate(now.getDate() - 90);
                    break;
                default:
                    break;
            }

            filtered = filtered.filter(submission => {
                const submissionDate = new Date(submission.submittedAt);
                return submissionDate >= startDate;
            });
        }

        setFilteredSubmissions(filtered);
        setCurrentPage(1); // Reset to first page when filters change
    }, [searchTerm, riskFilter, departmentFilter, dateFilter, userFilter, submissions]);

    // Get unique departments for filter dropdown with null checks
    const departments = [...new Set(submissions
        .map(submission => submission.staff?.department)
        .filter(dept => dept != null && dept !== '')
    )];

    // Get unique users for filter dropdown
    const uniqueUsers = Object.entries(userStats).map(([userId, user]) => ({
        id: userId,
        name: user.name,
        email: user.email,
        employeeId: user.employeeId,
        totalSubmissions: user.totalSubmissions
    })).sort((a, b) => b.totalSubmissions - a.totalSubmissions);

    // Format date to readable format
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get risk level badge color with null check
    const getRiskBadgeColor = (riskLevel) => {
        const level = riskLevel?.toLowerCase() || 'unknown';
        switch (level) {
            case 'high':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'medium':
            case 'moderate':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'low':
                return 'bg-green-100 text-green-800 border-green-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    // Handle view details click
    const handleViewDetails = (submission) => {
        setSelectedSubmission(submission);
        setShowModal(true);
    };

    // Close modal
    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedSubmission(null);
    };

    // Pagination handlers
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Export to CSV function - UPDATED to include Employee ID
    const exportToSimplifiedCSV = () => {
        if (filteredSubmissions.length === 0) return;

        // Find maximum number of questions across all submissions
        const maxQuestions = Math.max(...filteredSubmissions.map(sub =>
            sub.answers ? sub.answers.length : 0
        ));

        // Prepare CSV headers - INCLUDES EMPLOYEE ID
        const headers = [
            'Employee ID',
            'Employee Name',
            'Email ID',
            'Age',
            'Gender',
            'Contact Number',
            'Company Name',
            'Submission date & Time'
        ];

        // Add question columns (Q1, Q2, Q3, etc.)
        for (let i = 1; i <= maxQuestions; i++) {
            headers.push(`Q${i}`);
        }

        // Add Total Questions and Total Points at the end as requested
        headers.push('Total Questions');
        headers.push('Total Points');

        // Start CSV content
        let csvContent = headers.join(',') + '\n';

        // Add rows for each submission
        filteredSubmissions.forEach(submission => {
            const staff = submission.staff || {};
            const answers = submission.answers || [];
            const totalQuestions = answers.length;

            // Basic information row - INCLUDES EMPLOYEE ID
            const row = [
                `"${staff.employeeId || 'N/A'}"`,
                `"${staff.name || 'N/A'}"`,
                `"${staff.email || 'N/A'}"`,
                `"${staff.age || 'N/A'}"`,
                `"${staff.gender || 'N/A'}"`,
                `"${staff.mobile || 'N/A'}"`,
                `"${staff.company || 'N/A'}"`,
                `"${formatDate(submission.submittedAt)}"`
            ];

            // Add question answers - Only selected answers, not question text
            for (let i = 0; i < maxQuestions; i++) {
                if (i < answers.length) {
                    const answer = answers[i];
                    // Get only the selected answer/option text
                    const answerText = answer?.selectedOption?.text ||
                        answer?.answer ||
                        answer?.selectedAnswer ||
                        'No answer';
                    row.push(`"${answerText.replace(/"/g, '""')}"`);
                } else {
                    row.push('""');
                }
            }

            // Add Total Questions and Total Points at the end as requested
            row.push(`"${totalQuestions}"`);
            row.push(`"${submission.totalPoints || 0}"`);

            csvContent += row.join(',') + '\n';
        });

        // Download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `hra-submissions-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export user statistics to CSV - INCLUDES EMPLOYEE ID
    const exportUserStatsToCSV = () => {
        if (Object.keys(userStats).length === 0) return;

        let csvContent = 'USER SUBMISSION STATISTICS\n';
        csvContent += `Generated on: ${new Date().toLocaleDateString()}\n`;
        csvContent += '='.repeat(80) + '\n\n';

        // Updated headers to include Employee ID
        csvContent += 'Employee ID,Name,Email,Department,Age,Gender,Contact Number,Company,Total Submissions,Avg Points,First Submission,Last Submission,High Risk,Medium Risk,Low Risk\n';

        Object.values(userStats).forEach(user => {
            csvContent += `"${user.employeeId || 'N/A'}","${user.name || 'N/A'}","${user.email || 'N/A'}","${user.departments.join(', ') || 'N/A'}",`;
            csvContent += `"${user.age || 'N/A'}","${user.gender || 'N/A'}","${user.mobile || 'N/A'}","${user.company || 'N/A'}",`;
            csvContent += `${user.totalSubmissions},${user.avgTotalPoints},`;
            csvContent += `${formatDate(user.firstSubmission)},${formatDate(user.lastSubmission)},`;
            csvContent += `${user.riskDistribution.high},${user.riskDistribution.medium},${user.riskDistribution.low}\n`;
        });

        // Download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `hra-user-statistics-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setRiskFilter('all');
        setDepartmentFilter('all');
        setDateFilter('all');
        setUserFilter('all');
        setCurrentPage(1);
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            const half = Math.floor(maxPagesToShow / 2);
            let start = Math.max(1, currentPage - half);
            let end = Math.min(totalPages, start + maxPagesToShow - 1);

            if (end - start + 1 < maxPagesToShow) {
                start = Math.max(1, end - maxPagesToShow + 1);
            }

            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }
        }

        return pageNumbers;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <div className="text-red-600 font-semibold mb-2">Error</div>
                <div className="text-red-500 mb-4">{error}</div>
                <button
                    onClick={fetchSubmissions}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header with Export Buttons */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            HRA Submissions
                        </h1>
                        <p className="text-gray-600">
                            Showing {currentPageSubmissions.length} of {filteredSubmissions.length} submissions
                            {filteredSubmissions.length !== submissions.length && ` (filtered from ${submissions.length})`}
                            <span className="ml-2 text-blue-600 font-medium">
                                • Page {currentPage} of {totalPages}
                            </span>
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() => setShowUserStats(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            View User Stats
                        </button>
                        <button
                            onClick={exportToSimplifiedCSV}
                            disabled={filteredSubmissions.length === 0}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${filteredSubmissions.length === 0
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Export CSV
                        </button>
                    </div>
                </div>

                {/* Search and Filter Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
                        {/* Search Input */}
                        <div className="lg:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Search (Name, Email, Employee ID, Dept, Contact, Company)
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by name, email, employee ID, department, contact, company..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>

                        {/* User Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Filter by User
                            </label>
                            <select
                                value={userFilter}
                                onChange={(e) => setUserFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Users ({Object.keys(userStats).length})</option>
                                {uniqueUsers.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.employeeId ? `${user.employeeId} - ${user.name}` : user.name} ({user.totalSubmissions})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Risk Level Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Risk Level
                            </label>
                            <select
                                value={riskFilter}
                                onChange={(e) => setRiskFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Risk Levels</option>
                                <option value="high">High</option>
                                <option value="medium">Medium</option>
                                <option value="moderate">Moderate</option>
                                <option value="low">Low</option>
                            </select>
                        </div>

                        {/* Department Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Department
                            </label>
                            <select
                                value={departmentFilter}
                                onChange={(e) => setDepartmentFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Departments</option>
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>

                        {/* Date Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date Range
                            </label>
                            <select
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Time</option>
                                <option value="today">Today</option>
                                <option value="week">Last 7 Days</option>
                                <option value="month">Last 30 Days</option>
                            </select>
                        </div>

                        {/* Items Per Page */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Items Per Page
                            </label>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(parseInt(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="5">5 per page</option>
                                <option value="10">10 per page</option>
                                <option value="20">20 per page</option>
                                <option value="50">50 per page</option>
                            </select>
                        </div>
                    </div>

                    {/* Clear Filters Button */}
                    <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-600">
                            Total {filteredSubmissions.length} submission{filteredSubmissions.length !== 1 ? 's' : ''} found
                        </div>
                        <button
                            onClick={clearFilters}
                            className="text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors"
                        >
                            Clear All Filters
                        </button>
                    </div>
                </div>

                {/* User Filter Info */}
                {userFilter !== 'all' && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-semibold text-blue-900">
                                    Showing submissions for: {userStats[userFilter]?.name || 'Selected User'}
                                    {userStats[userFilter]?.employeeId && ` (ID: ${userStats[userFilter]?.employeeId})`}
                                </h3>
                                <p className="text-blue-700 text-sm mt-1">
                                    Total {userStats[userFilter]?.totalSubmissions || 0} submissions •
                                    Avg Points: {userStats[userFilter]?.avgTotalPoints || 0}
                                </p>
                            </div>
                            <button
                                onClick={() => setUserFilter('all')}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                                Clear User Filter
                            </button>
                        </div>
                    </div>
                )}

                {/* Table Container */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                        Staff Details (INCLUDES EMPLOYEE ID)
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                        Submission Date
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                        Total Points
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                        Risk Level
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                        User Submissions
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {currentPageSubmissions.map((submission, index) => {
                                    const staff = submission.staff || {};
                                    const userId = staff.employeeId || staff.email || staff.name || 'unknown';
                                    const userStat = userStats[userId];

                                    return (
                                        <tr
                                            key={submission.submissionId || index}
                                            className="hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="px-6 py-4">
                                                <div>
                                                    {/* Employee ID shown prominently */}
                                                    {staff.employeeId && (
                                                        <div className="mb-1">
                                                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 border border-gray-300">
                                                                ID: {staff.employeeId}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="font-semibold text-gray-900">
                                                        {staff.name || 'Unknown Staff'}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        {staff.email || 'N/A'}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Dept: {staff.department || 'Unknown Department'}
                                                        {staff.branch && ` • ${staff.branch}`}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Age: {staff.age || 'N/A'} • Gender: {staff.gender || 'N/A'}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Contact: {staff.mobile || 'N/A'} • Company: {staff.company || 'N/A'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {formatDate(submission.submittedAt)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                                    {submission.totalPoints || 0} Points
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRiskBadgeColor(submission.riskLevel)}`}>
                                                    {submission.riskLevel || 'Unknown'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                                                        {userStat?.totalSubmissions || 0} total
                                                    </span>
                                                    {userStat && userStat.totalSubmissions > 1 && (
                                                        <button
                                                            onClick={() => setUserFilter(userId)}
                                                            className="text-purple-600 hover:text-purple-800 text-sm font-medium transition-colors"
                                                            title="View all submissions by this user"
                                                        >
                                                            View All
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => handleViewDetails(submission)}
                                                    className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {currentPageSubmissions.length === 0 && (
                        <div className="text-center py-12">
                            <div className="text-gray-400 text-6xl mb-4">📋</div>
                            <div className="text-gray-500 text-lg mb-2">No submissions found</div>
                            {submissions.length > 0 && (
                                <p className="text-gray-400 text-sm">
                                    Try adjusting your search or filters
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Pagination Controls */}
                {filteredSubmissions.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                            <div className="text-sm text-gray-600">
                                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredSubmissions.length)} to {Math.min(currentPage * itemsPerPage, filteredSubmissions.length)} of {filteredSubmissions.length} entries
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Previous Button */}
                                <button
                                    onClick={handlePrevPage}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 rounded-lg border ${currentPage === 1
                                            ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    Previous
                                </button>

                                {/* Page Numbers */}
                                <div className="flex items-center gap-1">
                                    {getPageNumbers().map(pageNumber => (
                                        <button
                                            key={pageNumber}
                                            onClick={() => handlePageClick(pageNumber)}
                                            className={`w-10 h-10 flex items-center justify-center rounded-lg border ${currentPage === pageNumber
                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            {pageNumber}
                                        </button>
                                    ))}

                                    {/* Ellipsis for more pages */}
                                    {totalPages > 5 && currentPage < totalPages - 2 && (
                                        <span className="px-2 text-gray-500">...</span>
                                    )}
                                </div>

                                {/* Next Button */}
                                <button
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                    className={`px-3 py-1 rounded-lg border ${currentPage === totalPages
                                            ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* User Statistics Modal */}
                {showUserStats && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center p-6 border-b border-gray-200">
                                <h2 className="text-2xl font-bold text-gray-900">
                                    User Submission Statistics
                                </h2>
                                <button
                                    onClick={() => setShowUserStats(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                                        <div className="text-3xl font-bold text-blue-600 mb-2">
                                            {Object.keys(userStats).length}
                                        </div>
                                        <div className="text-blue-800 font-medium">Unique Users</div>
                                    </div>
                                    <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                                        <div className="text-3xl font-bold text-green-600 mb-2">
                                            {submissions.length}
                                        </div>
                                        <div className="text-green-800 font-medium">Total Submissions</div>
                                    </div>
                                    <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                                        <div className="text-3xl font-bold text-purple-600 mb-2">
                                            {submissions.length > 0 ? (submissions.length / Object.keys(userStats).length).toFixed(1) : 0}
                                        </div>
                                        <div className="text-purple-800 font-medium">Avg Submissions per User</div>
                                    </div>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Employee ID
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Department
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Age
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Gender
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Total Submissions
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Avg Points
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Risk Distribution
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Date Range
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {uniqueUsers.map(user => {
                                                const stats = userStats[user.id];
                                                return (
                                                    <tr key={user.id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4">
                                                            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded border border-gray-300">
                                                                {user.employeeId || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="font-medium text-gray-900">
                                                                {user.name}
                                                            </div>
                                                            <div className="text-sm text-gray-600">
                                                                {user.email}
                                                            </div>
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                Contact: {stats?.mobile || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="text-gray-900">
                                                                {stats?.departments?.join(', ') || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="font-medium text-gray-900">
                                                                {stats?.age || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="font-medium text-gray-900">
                                                                {stats?.gender || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                                                                {user.totalSubmissions} submissions
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="font-medium text-gray-900">
                                                                {stats?.avgTotalPoints || 0}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex flex-wrap gap-1">
                                                                {stats?.riskDistribution.high > 0 && (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                                                        High: {stats.riskDistribution.high}
                                                                    </span>
                                                                )}
                                                                {stats?.riskDistribution.medium > 0 && (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                                                        Medium: {stats.riskDistribution.medium}
                                                                    </span>
                                                                )}
                                                                {stats?.riskDistribution.low > 0 && (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                        Low: {stats.riskDistribution.low}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-600">
                                                            <div>
                                                                <div>First: {formatDate(stats?.firstSubmission)}</div>
                                                                <div>Last: {formatDate(stats?.lastSubmission)}</div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <button
                                                                onClick={() => {
                                                                    setUserFilter(user.id);
                                                                    setShowUserStats(false);
                                                                }}
                                                                className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                                                            >
                                                                View All
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="flex justify-between items-center p-6 border-t border-gray-200">
                                <button
                                    onClick={exportUserStatsToCSV}
                                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                                >
                                    Export Statistics
                                </button>
                                <button
                                    onClick={() => setShowUserStats(false)}
                                    className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Popup Modal (Existing) - UPDATED to include Employee ID */}
                {showModal && selectedSubmission && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            {/* Modal Header */}
                            <div className="flex justify-between items-center p-6 border-b border-gray-200">
                                <h2 className="text-2xl font-bold text-gray-900">
                                    Submission Details
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="p-6 space-y-6">
                                {/* Staff Information */}
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <h3 className="font-semibold text-gray-900 mb-3">Staff Information</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {/* Employee ID added here */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Employee ID</label>
                                            <p className="text-gray-900 font-mono">{selectedSubmission.staff?.employeeId || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Name</label>
                                            <p className="text-gray-900">{selectedSubmission.staff?.name || 'Unknown Staff'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Email</label>
                                            <p className="text-gray-900">{selectedSubmission.staff?.email || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Department</label>
                                            <p className="text-gray-900">{selectedSubmission.staff?.department || 'Unknown Department'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Branch</label>
                                            <p className="text-gray-900">{selectedSubmission.staff?.branch || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Age</label>
                                            <p className="text-gray-900">{selectedSubmission.staff?.age || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Gender</label>
                                            <p className="text-gray-900">{selectedSubmission.staff?.gender || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Contact Number</label>
                                            <p className="text-gray-900">{selectedSubmission.staff?.mobile || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Company</label>
                                            <p className="text-gray-900">{selectedSubmission.staff?.company || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Submission Information */}
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <h3 className="font-semibold text-gray-900 mb-3">Submission Information</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Submission Date</label>
                                            <p className="text-gray-900">{formatDate(selectedSubmission.submittedAt)}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Total Points</label>
                                            <p className="text-2xl font-bold text-blue-600">{selectedSubmission.totalPoints || 0}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600">Risk Level</label>
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRiskBadgeColor(selectedSubmission.riskLevel)}`}>
                                                {selectedSubmission.riskLevel || 'Unknown'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Risk Message */}
                                {selectedSubmission.riskMessage && (
                                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                                        <h3 className="font-semibold text-yellow-900 mb-2">Risk Assessment</h3>
                                        <p className="text-yellow-800">{selectedSubmission.riskMessage}</p>
                                    </div>
                                )}

                                {/* Category Points */}
                                {selectedSubmission.categoryPoints && Object.keys(selectedSubmission.categoryPoints).length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-3">Category Points</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {Object.entries(selectedSubmission.categoryPoints).map(([category, points]) => (
                                                <div key={category} className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                                                    <div className="font-medium text-gray-900 text-sm mb-2">{category}</div>
                                                    <div className="text-2xl font-bold text-blue-600">
                                                        {points} Points
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Health Recommendations */}
                                {selectedSubmission.prescribedForCategories && Object.keys(selectedSubmission.prescribedForCategories).length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-3">Health Recommendations</h3>
                                        <div className="space-y-3">
                                            {Object.entries(selectedSubmission.prescribedForCategories).map(([category, prescription]) => (
                                                <div key={category} className="bg-green-50 rounded-lg p-4 border border-green-200">
                                                    <div className="font-medium text-green-900 mb-2">{category}</div>
                                                    <div className="text-green-800">{prescription}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Questionnaire Responses */}
                                {selectedSubmission.answers && selectedSubmission.answers.length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-3">Questionnaire Responses</h3>
                                        <div className="space-y-4">
                                            {selectedSubmission.answers.map((answer, idx) => (
                                                <div
                                                    key={idx}
                                                    className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                                                >
                                                    {/* Category + Points */}
                                                    <div className="flex justify-between items-start mb-3">
                                                        <span className="font-medium text-gray-900 text-sm">
                                                            {answer.category || 'Uncategorized'}
                                                        </span>
                                                        <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                                                            {answer.scoredPoints || 0} Points
                                                        </span>
                                                    </div>

                                                    {/* Question Text */}
                                                    <div className="mb-3">
                                                        <p className="text-gray-800 font-medium">
                                                            Q{idx + 1}. {answer.questionText || "— Question not available —"}
                                                        </p>
                                                    </div>

                                                    {/* Selected Option */}
                                                    <div className="text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-200">
                                                        <strong className="text-gray-600">Selected Answer:</strong>
                                                        <p className="mt-1 text-gray-900">
                                                            {answer?.selectedOption?.text || "— No option selected —"}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="flex justify-end p-6 border-t border-gray-200">
                                <button
                                    onClick={handleCloseModal}
                                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HRASubmissionList;